// ForceGraph3DView.tsx
// 3D force-directed graph view using react-force-graph-3d (Three.js / WebGL)
// Supports expand/collapse of children via right-click.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import ForceGraph3D from 'react-force-graph-3d';
import type { ForceGraphMethods } from 'react-force-graph-3d';
import SpriteText from 'three-spritetext';
import type {
  NodeInstance,
  Graph,
  Relationship,
} from '../types';
import { getNodeBaseColor, scoreToHighlightColor } from '../utils/colors';

const PREVIEW_ACCENT = '#ff4fd8';

const resolveNodeBaseColor = (node: NodeInstance): string => {
  return getNodeBaseColor(node.color);
};

const escapeHtml = (value: string): string => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const formatNodeHoverLabel = (node: NodeInstance): string => {
  const name = (node.name || '').trim();
  const type = (node.type || '').trim();
  const titleText = name && type
    ? `${name} (${type})`
    : (name || type || 'Node');
  const titleLabel = escapeHtml(titleText);
  const description = (node.description || '')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '' && !/^BFO Class:/i.test(line) && !/^BFO Explanation:/i.test(line))
    .join('\n')
    .trim();

  if (description === '') {
    return `<div style="font-weight:600;">${titleLabel}</div>`;
  }

  return [
    `<div style="font-weight:600;">${titleLabel}</div>`,
    '<div style="height:8px;"></div>',
    `<div>${escapeHtml(description).replace(/\n/g, '<br/>')}</div>`,
  ].join('');
};

type BFOAssignment = {
  id: string;
  name: string;
  description: string;
  parentId: string | null;
};

type BFOOverlayNode = BFOAssignment & {
  x: number;
  y: number;
};

type TrackballControlsLike = {
  target?: { x: number; y: number; z: number };
  onTouchEnd?: (event: Event) => void;
};

const wrapOverlayText = (text: string, maxCharsPerLine: number): string[] => {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return [];
  }

  const lines: string[] = [];
  let currentLine = words[0];

  for (let index = 1; index < words.length; index += 1) {
    const nextWord = words[index];
    const candidate = `${currentLine} ${nextWord}`;
    if (candidate.length <= maxCharsPerLine) {
      currentLine = candidate;
      continue;
    }
    lines.push(currentLine);
    currentLine = nextWord;
  }

  lines.push(currentLine);
  return lines;
};

const BFO_OVERLAY_NODES: BFOOverlayNode[] = [
  { id: 'bfo-entity', name: 'Entity', description: 'Anything that exists.', parentId: null, x: 0.5, y: 0.08 },
  { id: 'bfo-continuant', name: 'Continuant', description: 'Persists through time while maintaining identity.', parentId: 'bfo-entity', x: 0.14, y: 0.24 },
  { id: 'bfo-occurrent', name: 'Occurrent', description: 'Happens or unfolds over time.', parentId: 'bfo-entity', x: 0.86, y: 0.24 },
  { id: 'bfo-independent-continuant', name: 'Independent Continuant', description: 'Exists on its own, not dependent on another bearer.', parentId: 'bfo-continuant', x: 0.04, y: 0.62 },
  { id: 'bfo-specifically-dependent-continuant', name: 'Specifically Dependent Continuant', description: 'Depends on a specific bearer to exist.', parentId: 'bfo-continuant', x: 0.34, y: 0.62 },
  { id: 'bfo-generically-dependent-continuant', name: 'Generically Dependent Continuant', description: 'Information-like content that can have multiple bearers.', parentId: 'bfo-continuant', x: 0.64, y: 0.62 },
  { id: 'bfo-process', name: 'Process', description: 'A temporally extended occurrent.', parentId: 'bfo-occurrent', x: 0.96, y: 0.62 },
  { id: 'bfo-role', name: 'Role', description: 'A specifically dependent continuant realized in contexts and processes.', parentId: 'bfo-specifically-dependent-continuant', x: 0.34, y: 0.92 },
];

const BFO_OVERLAY_NODE_BY_ID: Record<string, BFOOverlayNode> = Object.fromEntries(
  BFO_OVERLAY_NODES.map((node) => [node.id, node])
);

const BFO_SCENE_WIDTH = 760;
const BFO_SCENE_HEIGHT = 560;

const getBfoLineageIds = (nodeId: string | null): Set<string> => {
  const lineage = new Set<string>();
  let currentId = nodeId;

  while (currentId) {
    lineage.add(currentId);
    currentId = BFO_OVERLAY_NODE_BY_ID[currentId]?.parentId ?? null;
  }

  return lineage;
};

// ---- Types ------------------------------------------------------------------

interface InternalNode extends NodeInstance {
  val?: number;
  color?: string;
  collapsed?: boolean;
  isPreview?: boolean;
  // outgoing links from this node in the full graph
  childLinks: InternalLink[];
}

interface InternalLink {
  id: string;
  type: string;
  description?: string;
  semantic_summary?: string;
  summary_state?: string;
  source: string | InternalNode;
  target: string | InternalNode;
  _parentId: string; // immutable: always the parent node id
  _childId: string;  // immutable: always the child node id
}

const getRelationshipCompositeKey = (relationship: Pick<Relationship, 'from_node_id' | 'to_node_id' | 'type'>): string => {
  return `${relationship.from_node_id}|${relationship.to_node_id}|${relationship.type}`;
};

const formatRelationshipHoverLabel = (relationship: Pick<Relationship, 'type' | 'description' | 'semantic_summary'>): string => {
  const typeLabel = escapeHtml((relationship.type || 'Relationship').trim() || 'Relationship');
  const detail = (relationship.description || relationship.semantic_summary || '').trim();

  if (detail === '') {
    return `<div style="font-weight:600;">${typeLabel}</div>`;
  }

  return [
    `<div style="font-weight:600;">${typeLabel}</div>`,
    '<div style="height:8px;"></div>',
    `<div>${escapeHtml(detail).replace(/\n/g, '<br/>')}</div>`,
  ].join('');
};

interface ForceGraph3DViewProps {
  graphData: Graph | null;
  renderMode?: 'tree' | 'network';
  onNodeClick: (node: NodeInstance) => void;
  onBackgroundClick?: () => void;
  /** When provided, clicking a node also updates the focus node (URL + focus highlight). */
  setFocusNodeId?: (id: string) => void;
  /** Initial focus node ID — the camera will center on this node once positions are available. */
  focusNodeId?: string | null;
  /** Highlighted nodes. The focus node is passed here so it stays visually distinct. */
  highlightedNodeScores?: Map<string, number>;
  /** Candidate IDs to highlight for relationship creation. */
  relationshipCandidateNodeIds?: string[];
  /** Optional right-click handler for nodes. */
  onNodeContextMenu?: (
    node: { id: string; name?: string },
    position: { x: number; y: number }
  ) => void;
  /** Optional right-click handler for links/edges. */
  onLinkContextMenu?: (link: { id: string; type: string; sourceId: string; targetId: string }, position: { x: number; y: number }) => void;
  /** IDs of ghost nodes — kept in graph but rendered invisible. */
  ghostNodeIds?: Set<string>;
  /** IDs of nodes that exist only in the current preview graph. */
  previewNodeIds?: Set<string>;
  /** Relationship keys (`from|to|type`) that exist only in the current preview graph. */
  previewRelationshipKeys?: Set<string>;
  /** Incrementing key that forces a full layout reset (clears saved positions). */
  layoutKey?: number;
  schemaMode?: boolean;
  hoveredSchemaNodeId?: string | null;
  onHoveredSchemaNodeChange?: (nodeId: string | null) => void;
  schemaBfoAssignments?: Record<string, BFOAssignment> | null;
}

// ---- Helpers ----------------------------------------------------------------

// Nodes as mutated by react-force-graph-3d (adds x/y/z/vx/vy/vz/fx/fy/fz in-place)
type SimNode3D = InternalNode & {
  x?: number; y?: number; z?: number;
  fx?: number; fy?: number; fz?: number;
};

type TargetPosition3D = {
  x: number;
  y: number;
  z: number;
};

// Detect WebGL availability once at module load.
const isWebGLAvailable = (() => {
  try {
    const canvas = document.createElement('canvas');
    return !!(canvas.getContext('webgl2') || canvas.getContext('webgl'));
  } catch {
    return false;
  }
})();

const ForceGraph3DView: React.FC<ForceGraph3DViewProps> = ({
  graphData,
  renderMode = 'tree',
  onNodeClick,
  onBackgroundClick,
  setFocusNodeId,
  focusNodeId,
  highlightedNodeScores = new Map(),
  relationshipCandidateNodeIds = [],
  onNodeContextMenu,
  onLinkContextMenu,
  ghostNodeIds = new Set(),
  previewNodeIds = new Set(),
  previewRelationshipKeys = new Set(),
  layoutKey = 0,
  schemaMode = false,
  hoveredSchemaNodeId = null,
  onHoveredSchemaNodeChange,
  schemaBfoAssignments = null,
}) => {
  // Ref to the underlying ForceGraph3D instance — used to patch TrackballControls.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fgRef = useRef<ForceGraphMethods<object, object> | undefined>(undefined);

  // Container ref + measured dimensions so the Three.js canvas matches the
  // actual container size rather than defaulting to window.innerWidth/Height.
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setDimensions({ width: Math.round(width), height: Math.round(height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Full internal graph — rebuilt when graphData prop changes
  const allNodesById = useRef<Record<string, InternalNode>>({});
  const allLinksByKey = useRef<Record<string, InternalLink>>({});
  const rootNodeIds = useRef<string[]>([]);
  // Track layoutKey so we can detect when a full re-layout is requested
  const layoutKeyRef = useRef(layoutKey);

  // Track whether we have already auto-centered on the initial focus node.
  const didAutoFocusRef = useRef(false);

  // Track the currently focused node so we can re-center the orbit pivot on it
  // when the user starts a rotation after having panned away.
  const focusedNodeIdRef = useRef<string | null>(null);

  // Keep a ref that always reflects the current focusNodeId prop so
  // imperative callbacks (which react-force-graph may cache) never read
  // a stale closure value.
  const focusNodeIdRef = useRef(focusNodeId);
  focusNodeIdRef.current = focusNodeId;

  const isTreeMode = renderMode === 'tree';
  const [displayedHoveredSchemaNodeId, setDisplayedHoveredSchemaNodeId] = useState<string | null>(null);
  const [isBfoLegendHovered, setIsBfoLegendHovered] = useState(false);
  const bfoHighlightDelayTimeoutRef = useRef<number | null>(null);
  const previewLinkIds = useRef<Set<string>>(new Set());
  const localRelayoutNodeIdsRef = useRef<Set<string>>(new Set());
  const localRelayoutTicksRemainingRef = useRef(0);
  const localRelayoutTargetPositionsRef = useRef<Record<string, TargetPosition3D>>({});
  const lastFocusedNodeIdRef = useRef<string | null>(focusNodeId ?? null);
  const hoveredBfoClassId = displayedHoveredSchemaNodeId ? (schemaBfoAssignments?.[displayedHoveredSchemaNodeId]?.id ?? null) : null;
  const highlightedBfoLineageIds = hoveredBfoClassId ? getBfoLineageIds(hoveredBfoClassId) : new Set<string>();
  const emphasizeEntireBfoUnderlay = isBfoLegendHovered;
  const showBfoOverlay = schemaMode && renderMode === 'network';

  const clearBfoHighlightDelayTimeout = useCallback(() => {
    if (bfoHighlightDelayTimeoutRef.current !== null) {
      window.clearTimeout(bfoHighlightDelayTimeoutRef.current);
      bfoHighlightDelayTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => () => {
    clearBfoHighlightDelayTimeout();
  }, [clearBfoHighlightDelayTimeout]);

  useEffect(() => {
    if (!schemaMode) {
      clearBfoHighlightDelayTimeout();
      setDisplayedHoveredSchemaNodeId(null);
      return;
    }

    if (!hoveredSchemaNodeId) {
      clearBfoHighlightDelayTimeout();
      setDisplayedHoveredSchemaNodeId(null);
      return;
    }

    clearBfoHighlightDelayTimeout();
    bfoHighlightDelayTimeoutRef.current = window.setTimeout(() => {
      setDisplayedHoveredSchemaNodeId(hoveredSchemaNodeId);
      bfoHighlightDelayTimeoutRef.current = null;
    }, 1000);

    return () => {
      clearBfoHighlightDelayTimeout();
    };
  }, [clearBfoHighlightDelayTimeout, hoveredSchemaNodeId, schemaMode]);

  const moveCameraToNode = useCallback((node: SimNode3D, distanceOverride?: number, duration = 1200) => {
    if (!fgRef.current || node.x == null || node.y == null || node.z == null) return false;
    const camera = fgRef.current.camera?.();
    const controls = fgRef.current.controls?.() as TrackballControlsLike | undefined;
    if (!camera || !controls?.target) return false;

    const currentOffset = {
      x: camera.position.x - controls.target.x,
      y: camera.position.y - controls.target.y,
      z: camera.position.z - controls.target.z,
    };
    const currentDistance = Math.sqrt(
      currentOffset.x * currentOffset.x +
      currentOffset.y * currentOffset.y +
      currentOffset.z * currentOffset.z
    );
    const nextDistance = distanceOverride && distanceOverride > 0 ? distanceOverride : currentDistance;
    const scale = currentDistance > 0 ? nextDistance / currentDistance : 1;
    const nextOffset = {
      x: currentOffset.x * scale,
      y: currentOffset.y * scale,
      z: currentOffset.z * scale,
    };
    fgRef.current.cameraPosition(
      {
        x: node.x + nextOffset.x,
        y: node.y + nextOffset.y,
        z: node.z + nextOffset.z,
      },
      { x: node.x, y: node.y, z: node.z },
      duration,
    );
    return true;
  }, []);

  // Patch TrackballControls.onTouchEnd to guard against a Three.js bug where
  // DragControls fires onPointerCancel → onPointerUp → onTouchEnd after a touch
  // is already removed from the pointer list, causing _getSecondPointerPosition()
  // to return undefined and reading `.x` to throw.
  useEffect(() => {
    const controls = fgRef.current?.controls?.() as TrackballControlsLike | undefined;
    if (!controls) return;

    if (typeof controls.onTouchEnd === 'function') {
      const orig = controls.onTouchEnd as (e: Event) => void;
      controls.onTouchEnd = function (event: Event) {
        try { orig.call(this, event); } catch { /* pointer already removed — safe to ignore */ }
      };
    }
  // Re-run when the canvas first appears (dimensions gate ForceGraph3D rendering).
  }, [dimensions.width > 0 && dimensions.height > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  // Configure d3 forces per render mode. Tree mode stays compact for the
  // rooted hierarchy view, while network mode gives schema graphs more room.
  useEffect(() => {
    const fg = fgRef.current;
    if (!fg) return;
    fg.d3Force('charge')?.strength(isTreeMode ? -60 : -180);
    fg.d3Force('link')?.distance(isTreeMode ? 35 : 90);
  // Re-run once the canvas is ready (same gate as the TrackballControls patch).
  }, [dimensions.width > 0 && dimensions.height > 0, isTreeMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Visible (pruned) graph fed to the renderer
  const [visibleData, setVisibleData] = useState<{ nodes: InternalNode[]; links: InternalLink[] }>({
    nodes: [],
    links: [],
  });

  const getVisibleNetworkData = useCallback((): { nodes: InternalNode[]; links: InternalLink[] } => {
    return {
      nodes: Object.values(allNodesById.current),
      links: Object.values(allLinksByKey.current),
    };
  }, []);

  // Walk the full graph, honouring each node's `collapsed` flag.
  // Mirrors the getPrunedTree pattern from the vasturiano example.
  const getPrunedTree = useCallback((): { nodes: InternalNode[]; links: InternalLink[] } => {
    const nodesById = allNodesById.current;
    const visibleNodes: InternalNode[] = [];
    const visibleLinks: InternalLink[] = [];
    const visited = new Set<string>();
    const visibleLinkIds = new Set<string>();

    function traverse(node: InternalNode): void {
      if (visited.has(node.id)) return;
      visited.add(node.id);
      visibleNodes.push(node);
      if (node.collapsed) return;
      for (const link of node.childLinks) {
        if (!visibleLinkIds.has(link.id)) {
          visibleLinks.push(link);
          visibleLinkIds.add(link.id);
        }
        // Use _childId — source/target are mutated to objects by force-graph after simulation
        const child = nodesById[link._childId];
        if (child) traverse(child);
      }
    }

    for (const rootId of rootNodeIds.current) {
      const root = nodesById[rootId];
      if (root) traverse(root);
    }

    // Force preview nodes to be visible even if their parent is collapsed.
    // Self-loop and root-detection cases are handled upstream (childIds excludes
    // self-loops), so this only needs to cover the collapsed-parent edge case.
    for (const previewNodeId of previewNodeIds) {
      const previewNode = nodesById[previewNodeId];
      if (previewNode && !visited.has(previewNodeId)) {
        traverse(previewNode);
      }
    }

    return { nodes: visibleNodes, links: visibleLinks };
  }, [previewNodeIds]);

  // Rebuild the internal graph whenever the raw graphData prop changes.
  // Tree mode preserves pinned positions to avoid full-layout churn during
  // incremental graph edits. Network mode keeps prior positions only as seeds
  // so the engine can spread schema nodes on each rebuild.
  useEffect(() => {
    if (!graphData) return;
    // Reset auto-focus so a fresh graph load re-centers on the focus node.
    didAutoFocusRef.current = false;

    // Detect whether a full re-layout was requested (layoutKey changed)
    const fullRelayout = layoutKey !== layoutKeyRef.current;
    layoutKeyRef.current = layoutKey;

    const previousNodesById = fullRelayout ? {} : allNodesById.current;
    const nodesById: Record<string, InternalNode> = {};
    const newlyAddedNodeIds = new Set<string>();

    const localRelayoutNodeIds = new Set<string>();
    for (const previewNodeId of previewNodeIds) {
      localRelayoutNodeIds.add(previewNodeId);
    }
    localRelayoutTargetPositionsRef.current = {};

    // Reconcile node objects in place so unchanged nodes keep simulation identity.
    for (const n of graphData.nodes ?? []) {
      const prev = previousNodesById[n.id] as SimNode3D | undefined;
      const isPreviewNode = previewNodeIds.has(n.id);
      const node: SimNode3D = prev ?? {
        ...n,
        color: resolveNodeBaseColor(n),
        val: isPreviewNode ? 7.5 : 4,
        collapsed: false,
        isPreview: isPreviewNode,
        childLinks: [],
      };

      if (!prev) {
        newlyAddedNodeIds.add(n.id);
      }

      node.id = n.id;
      node.type = n.type;
      node.name = n.name;
      node.description = n.description;
      node.semantic_summary = n.semantic_summary;
      node.summary_state = n.summary_state;
      node.aliases = n.aliases;
      node.properties = n.properties;
      node.particular = n.particular;
      node.pending_property_names = n.pending_property_names;
      node.pending_property_values = n.pending_property_values;
      node.color = resolveNodeBaseColor(n);
      node.val = isPreviewNode ? 7.5 : 4;
      node.isPreview = isPreviewNode;
      node.childLinks = [];

      if (fullRelayout) {
        delete node.fx;
        delete node.fy;
        delete node.fz;
      } else if (isTreeMode && node.x != null && node.y != null && node.z != null && !localRelayoutNodeIds.has(n.id)) {
        // Keep established nodes fixed in tree mode so local graph edits do not
        // collapse the user's current spatial context.
        node.fx = node.x;
        node.fy = node.y;
        node.fz = node.z;
      } else if (!isTreeMode || localRelayoutNodeIds.has(n.id)) {
        delete node.fx;
        delete node.fy;
        delete node.fz;
      }

      nodesById[n.id] = node as InternalNode;
    }

    const nextLinksByKey: Record<string, InternalLink> = {};
    const links: InternalLink[] = [];
    let hasNewLinks = false;

    for (const relationship of graphData.relationships ?? []) {
      const relationshipKey = getRelationshipCompositeKey(relationship);
      const existingLink = fullRelayout ? undefined : allLinksByKey.current[relationshipKey];
      if (!existingLink) {
        hasNewLinks = true;
      }
      const link: InternalLink = existingLink ?? {
        id: relationship.id || relationshipKey,
        type: relationship.type,
        description: relationship.description,
        semantic_summary: relationship.semantic_summary,
        summary_state: relationship.summary_state,
        source: nodesById[relationship.from_node_id] ?? relationship.from_node_id,
        target: nodesById[relationship.to_node_id] ?? relationship.to_node_id,
        _parentId: relationship.to_node_id,
        _childId: relationship.from_node_id,
      };

      link.id = relationship.id || relationshipKey;
      link.type = relationship.type;
      link.description = relationship.description;
      link.semantic_summary = relationship.semantic_summary;
      link.summary_state = relationship.summary_state;
      // Arrows follow schema definitions: source_id -> target_id.
      link.source = nodesById[relationship.from_node_id] ?? relationship.from_node_id;
      link.target = nodesById[relationship.to_node_id] ?? relationship.to_node_id;
      // Keep traversal semantics for expand/collapse rooted hierarchy.
      link._parentId = relationship.to_node_id;
      link._childId = relationship.from_node_id;

      nextLinksByKey[relationshipKey] = link;
      links.push(link);
    }

    const adjacency = new Map<string, string[]>();
    for (const link of links) {
      const fromNeighbors = adjacency.get(link._childId) ?? [];
      fromNeighbors.push(link._parentId);
      adjacency.set(link._childId, fromNeighbors);

      const toNeighbors = adjacency.get(link._parentId) ?? [];
      toNeighbors.push(link._childId);
      adjacency.set(link._parentId, toNeighbors);
    }

    localRelayoutNodeIdsRef.current = localRelayoutNodeIds;
    localRelayoutTicksRemainingRef.current = localRelayoutNodeIds.size > 0 ? 45 : 0;

    previewLinkIds.current = new Set(
      links
        .filter((link) => previewRelationshipKeys.has(getRelationshipCompositeKey({
          from_node_id: link._childId,
          to_node_id: link._parentId,
          type: link.type,
        })))
        .map((link) => link.id)
    );

    // Attach outgoing links to each PARENT node using immutable _parentId.
    // Self-loops are excluded from childIds: a node whose only parent is itself
    // must still be treated as a tree root, not a child.
    const childIds = new Set(links.filter((l) => l._childId !== l._parentId).map((l) => l._childId));
    for (const link of links) {
      if (link._childId === link._parentId) continue; // skip self-loops in tree structure
      const parent = nodesById[link._parentId];
      if (parent) parent.childLinks.push(link);
    }

    // Root nodes = nodes that are never a child.
    rootNodeIds.current = isTreeMode
      ? Object.keys(nodesById).filter((id) => !childIds.has(id))
      : [];

    const previewSiblingGroups = new Map<string, string[]>();
    for (const node of Object.values(nodesById) as SimNode3D[]) {
      if (!previewNodeIds.has(node.id)) {
        continue;
      }
      const positionedNeighborIds = (adjacency.get(node.id) ?? [])
        .filter((neighborId) => {
          const neighbor = nodesById[neighborId] as SimNode3D | undefined;
          return !!neighbor && neighbor.x != null && neighbor.y != null && neighbor.z != null;
        })
        .sort();
      if (positionedNeighborIds.length === 0) {
        continue;
      }
      const groupKey = positionedNeighborIds.join('|');
      const siblings = previewSiblingGroups.get(groupKey) ?? [];
      siblings.push(node.id);
      previewSiblingGroups.set(groupKey, siblings);
    }

    // Seed only truly new nodes near already-positioned neighbours so preview
    // additions land in the right neighbourhood without disturbing established nodes.
    for (const node of Object.values(nodesById) as SimNode3D[]) {
      if (!newlyAddedNodeIds.has(node.id) || (node.x != null && node.y != null && node.z != null)) {
        continue;
      }
      const positionedNeighbors = (adjacency.get(node.id) ?? [])
        .map((neighborId) => nodesById[neighborId] as SimNode3D | undefined)
        .filter((candidate): candidate is SimNode3D => (
          !!candidate && candidate.x != null && candidate.y != null && candidate.z != null
        ));
      if (positionedNeighbors.length === 0) {
        continue;
      }

      const anchor = positionedNeighbors.reduce(
        (acc, neighbor) => ({
          x: acc.x + (neighbor.x ?? 0),
          y: acc.y + (neighbor.y ?? 0),
          z: acc.z + (neighbor.z ?? 0),
        }),
        { x: 0, y: 0, z: 0 }
      );
      const anchorX = anchor.x / positionedNeighbors.length;
      const anchorY = anchor.y / positionedNeighbors.length;
      const anchorZ = anchor.z / positionedNeighbors.length;

      if (previewNodeIds.has(node.id)) {
        const positionedNeighborIds = (adjacency.get(node.id) ?? [])
          .filter((neighborId) => {
            const neighbor = nodesById[neighborId] as SimNode3D | undefined;
            return !!neighbor && neighbor.x != null && neighbor.y != null && neighbor.z != null;
          })
          .sort();
        const groupKey = positionedNeighborIds.join('|');
        const siblings = previewSiblingGroups.get(groupKey) ?? [node.id];
        const siblingIndex = Math.max(0, siblings.indexOf(node.id));
        const siblingCount = Math.max(1, siblings.length);
        const angle = (Math.PI * 2 * siblingIndex) / siblingCount;
        const ringRadius = Math.max(14, 10 + siblingCount * 4);
        const verticalOffset = siblingCount > 1 ? (siblingIndex - (siblingCount - 1) / 2) * 6 : 0;

        node.x = anchorX + Math.cos(angle) * ringRadius;
        node.y = anchorY + verticalOffset;
        node.z = anchorZ + Math.sin(angle) * ringRadius;
        localRelayoutTargetPositionsRef.current[node.id] = {
          x: node.x,
          y: node.y,
          z: node.z,
        };
      } else {
        const jitter = () => (Math.random() - 0.5) * 30;
        node.x = anchorX + jitter();
        node.y = anchorY + jitter();
        node.z = anchorZ + jitter();
      }
    }

    allNodesById.current = nodesById;
    allLinksByKey.current = nextLinksByKey;

    setVisibleData(
      isTreeMode
        ? getPrunedTree()
        : getVisibleNetworkData()
    );

    if (!fullRelayout && (newlyAddedNodeIds.size > 0 || hasNewLinks) && localRelayoutNodeIds.size > 0) {
      // Incremental preview updates preserve most positions, so explicitly
      // reheat the simulation to give the local slice a chance to settle.
      queueMicrotask(() => {
        fgRef.current?.d3ReheatSimulation?.();
      });
    }
  }, [getPrunedTree, getVisibleNetworkData, graphData, isTreeMode, layoutKey, previewNodeIds, previewRelationshipKeys]);

  useEffect(() => {
    if (!focusNodeId) return;
    const target = allNodesById.current[focusNodeId] as SimNode3D | undefined;
    if (!target || target.x == null || target.y == null || target.z == null) return;

    const previousFocusNodeId = lastFocusedNodeIdRef.current;
    lastFocusedNodeIdRef.current = focusNodeId;

    const isPreviewFocusChange =
      previewNodeIds.has(focusNodeId) &&
      previousFocusNodeId !== focusNodeId;

    focusedNodeIdRef.current = focusNodeId;

    if (isPreviewFocusChange) {
      return;
    }

    if (moveCameraToNode(target, undefined, 1500)) {
      didAutoFocusRef.current = true;
    }
  }, [focusNodeId, moveCameraToNode, previewNodeIds]);

  // Left-click: select node and smoothly animate the orbit pivot to it.
  // Keeps the same camera-to-target offset so the viewing angle is preserved.
  const handleNodeClick = useCallback(
    (node: object) => {
      const n = node as SimNode3D;
      if (ghostNodeIds.has(n.id)) return; // ghost nodes are not interactive
      if (schemaMode) {
        onHoveredSchemaNodeChange?.(n.id);
      }
      console.debug('[RelDebug] 3D node click', { nodeId: n.id });
      onNodeClick(n);
      if (setFocusNodeId) setFocusNodeId(n.id);
      focusedNodeIdRef.current = n.id;
      moveCameraToNode(n, undefined, 3000);
    },
    [ghostNodeIds, moveCameraToNode, onHoveredSchemaNodeChange, onNodeClick, schemaMode, setFocusNodeId]
  );

  const handleNodeHover = useCallback(
    (node: object | null) => {
      if (!schemaMode) {
        return;
      }
      const hoveredNode = node as SimNode3D | null;
      if (!hoveredNode || ghostNodeIds.has(hoveredNode.id)) {
        onHoveredSchemaNodeChange?.(null);
        return;
      }
      onHoveredSchemaNodeChange?.(hoveredNode.id);
    },
    [ghostNodeIds, onHoveredSchemaNodeChange, schemaMode]
  );

  // Right-click: toggle collapse on nodes with children.
  const handleNodeRightClick = useCallback(
    (node: object, event: MouseEvent) => {
      event.preventDefault();
      const n = node as InternalNode;
      if (!schemaMode && onNodeContextMenu) {
        if (ghostNodeIds.has(n.id)) return;
        onNodeContextMenu(
          { id: n.id, name: n.name },
          { x: event.clientX, y: event.clientY }
        );
        return;
      }
      if (!isTreeMode) return;
      if (ghostNodeIds.has(n.id)) return; // ghost nodes are not interactive
      const stored = allNodesById.current[n.id];
      if (!stored || stored.childLinks.length === 0) return;
      stored.collapsed = !stored.collapsed;
      setVisibleData(getPrunedTree());
    },
    [getPrunedTree, ghostNodeIds, isTreeMode, onNodeContextMenu, schemaMode]
  );

  // Right-click on a link: fire the link context menu callback.
  const handleLinkRightClick = useCallback(
    (link: object, event: MouseEvent) => {
      event.preventDefault();
      if (!onLinkContextMenu) return;
      const l = link as InternalLink;
      onLinkContextMenu(
        { id: l.id, type: l.type, sourceId: l._parentId, targetId: l._childId },
        { x: event.clientX, y: event.clientY },
      );
    },
    [onLinkContextMenu],
  );

  const isPreviewLink = useCallback((link: InternalLink): boolean => {
    return previewLinkIds.current.has(link.id);
  }, []);

  const handleEngineTick = useCallback(() => {
    if (localRelayoutTicksRemainingRef.current > 0) {
      for (const nodeId of localRelayoutNodeIdsRef.current) {
        const node = allNodesById.current[nodeId] as SimNode3D | undefined;
        const target = localRelayoutTargetPositionsRef.current[nodeId];
        if (!node || !target || node.x == null || node.y == null || node.z == null) {
          continue;
        }

        const blend = 0.18;
        node.x += (target.x - node.x) * blend;
        node.y += (target.y - node.y) * blend;
        node.z += (target.z - node.z) * blend;
      }

      localRelayoutTicksRemainingRef.current -= 1;
      if (localRelayoutTicksRemainingRef.current === 0) {
        for (const nodeId of localRelayoutNodeIdsRef.current) {
          const node = allNodesById.current[nodeId] as SimNode3D | undefined;
          if (!node || node.x == null || node.y == null || node.z == null) {
            continue;
          }
          node.fx = node.x;
          node.fy = node.y;
          node.fz = node.z;
        }
        localRelayoutTargetPositionsRef.current = {};
      }
    }

    if (!focusNodeIdRef.current || didAutoFocusRef.current) {
      return;
    }
    const target = allNodesById.current[focusNodeIdRef.current] as SimNode3D | undefined;
    if (!target || target.x == null || target.y == null || target.z == null) {
      return;
    }
    if (moveCameraToNode(target, undefined, 1500)) {
      didAutoFocusRef.current = true;
    }
  }, [moveCameraToNode]);

  const handleEngineStop = useCallback(() => {
    if (localRelayoutTicksRemainingRef.current > 0) {
      localRelayoutTicksRemainingRef.current = 0;
      for (const nodeId of localRelayoutNodeIdsRef.current) {
        const node = allNodesById.current[nodeId] as SimNode3D | undefined;
        if (!node || node.x == null || node.y == null || node.z == null) {
          continue;
        }
        node.fx = node.x;
        node.fy = node.y;
        node.fz = node.z;
      }
      localRelayoutTargetPositionsRef.current = {};
    }

    if (!focusNodeIdRef.current || didAutoFocusRef.current) {
      return;
    }
    const target = allNodesById.current[focusNodeIdRef.current] as SimNode3D | undefined;
    if (!target || target.x == null || target.y == null || target.z == null) {
      return;
    }
    if (moveCameraToNode(target, undefined, 1500)) {
      didAutoFocusRef.current = true;
    }
  }, [moveCameraToNode]);

  if (!isWebGLAvailable) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#888', gap: 12 }}>
        <p style={{ margin: 0, fontWeight: 500 }}>3D view requires WebGL</p>
        <p style={{ margin: 0, fontSize: 13 }}>
          Your browser or device does not support WebGL. Please switch to the <strong>Network</strong> view.
        </p>
      </div>
    );
  }

  if (!graphData || graphData.nodes.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#888' }}>
        No graph data available.
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%', position: 'relative' }}>
      {dimensions.width > 0 && dimensions.height > 0 && (
        <svg
          width={dimensions.width}
          height={dimensions.height}
          viewBox={`0 0 ${BFO_SCENE_WIDTH} ${BFO_SCENE_HEIGHT}`}
          preserveAspectRatio="xMidYMid meet"
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            pointerEvents: 'none',
            background: '#000000',
          }}
          aria-hidden="true"
        >
          <rect x="0" y="0" width={BFO_SCENE_WIDTH} height={BFO_SCENE_HEIGHT} fill="#000000" />
          {showBfoOverlay && (
            <g transform="translate(72 54)">
              {BFO_OVERLAY_NODES.map((node) => {
                if (!node.parentId) {
                  return null;
                }
                const parent = BFO_OVERLAY_NODE_BY_ID[node.parentId];
                if (!parent) {
                  return null;
                }
                const isLineHighlighted = emphasizeEntireBfoUnderlay || (highlightedBfoLineageIds.has(node.id) && highlightedBfoLineageIds.has(parent.id));
                const isLineDimmed = !emphasizeEntireBfoUnderlay && hoveredBfoClassId !== null && !isLineHighlighted;
                return (
                  <line
                    key={`${parent.id}-${node.id}`}
                    x1={parent.x * 616}
                    y1={parent.y * 452}
                    x2={node.x * 616}
                    y2={node.y * 452}
                    stroke={isLineHighlighted ? 'rgba(255,255,255,0.72)' : isLineDimmed ? 'rgba(120,130,150,0.04)' : 'rgba(170,180,195,0.08)'}
                    strokeWidth={isLineHighlighted ? 2.2 : 1.1}
                    style={{ transition: 'stroke 700ms ease, stroke-width 700ms ease' }}
                  />
                );
              })}
              {BFO_OVERLAY_NODES.map((node) => {
                const isNodeHighlighted = emphasizeEntireBfoUnderlay || highlightedBfoLineageIds.has(node.id);
                const isNodeDimmed = !emphasizeEntireBfoUnderlay && hoveredBfoClassId !== null && !isNodeHighlighted;
                const cardWidth = 156;
                const horizontalPadding = 12;
                const topPadding = 12;
                const bottomPadding = 12;
                const titleLineHeight = 13;
                const descriptionLineHeight = 10;
                const titleLines = wrapOverlayText(node.name, 24);
                const descriptionLines = wrapOverlayText(node.description, 34);
                const cardHeight = Math.max(
                  58,
                  topPadding + (titleLines.length * titleLineHeight) + 5 + (descriptionLines.length * descriptionLineHeight) + bottomPadding
                );
                const centerX = node.x * 616;
                const centerY = node.y * 452;
                const x = centerX - (cardWidth / 2);
                const y = centerY - (cardHeight / 2);
                let textY = y + topPadding + 9;

                return (
                  <g key={node.id} opacity={isNodeHighlighted ? 0.9 : isNodeDimmed ? 0.08 : 0.16} style={{ transition: 'opacity 700ms ease' }}>
                    <rect
                      x={x}
                      y={y}
                      width={cardWidth}
                      height={cardHeight}
                      rx={14}
                      fill={isNodeHighlighted ? 'rgba(226,232,240,1)' : 'rgba(70,78,94,0.24)'}
                      stroke={isNodeHighlighted ? 'rgba(255,255,255,0.82)' : 'rgba(160,170,190,0.1)'}
                      strokeWidth={isNodeHighlighted ? 2 : 1}
                      style={{ transition: 'fill 700ms ease, stroke 700ms ease, stroke-width 700ms ease' }}
                    />
                    {titleLines.map((line, index) => {
                      const currentY = textY;
                      textY += titleLineHeight;
                      return (
                        <text
                          key={`${node.id}-title-${index}`}
                          x={x + horizontalPadding}
                          y={currentY}
                          fill={isNodeHighlighted ? '#111827' : 'rgba(255,255,255,0.62)'}
                          fontSize="10.5"
                          fontWeight="700"
                        >
                          {line}
                        </text>
                      );
                    })}
                    {(() => {
                      textY += 5;
                      return null;
                    })()}
                    {descriptionLines.map((line, index) => {
                      const currentY = textY;
                      textY += descriptionLineHeight;
                      return (
                        <text
                          key={`${node.id}-desc-${index}`}
                          x={x + horizontalPadding}
                          y={currentY}
                          fill={isNodeHighlighted ? 'rgba(17,24,39,0.78)' : 'rgba(230,236,244,0.34)'}
                          fontSize="8.5"
                        >
                          {line}
                        </text>
                      );
                    })}
                  </g>
                );
              })}
            </g>
          )}
        </svg>
      )}
      <div style={{
        position: 'absolute', bottom: 8, left: 8, zIndex: 3,
        color: 'rgba(255,255,255,0.5)', fontSize: 11, pointerEvents: 'none',
      }}>
        Left-click: drag, details, and center &nbsp;|&nbsp; Right-click: pan &nbsp;|&nbsp; Right-line-click: hide
      </div>
      {showBfoOverlay && (
        <div
          style={{
            position: 'absolute',
            right: 12,
            bottom: 10,
            zIndex: 3,
            pointerEvents: 'auto',
          }}
          onMouseEnter={() => setIsBfoLegendHovered(true)}
          onMouseLeave={() => setIsBfoLegendHovered(false)}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: isBfoLegendHovered ? '8px 12px' : '8px 10px',
              borderRadius: 999,
              background: isBfoLegendHovered ? 'rgba(20,26,38,0.88)' : 'rgba(20,26,38,0.62)',
              border: isBfoLegendHovered ? '1px solid rgba(255,255,255,0.28)' : '1px solid rgba(255,255,255,0.14)',
              color: 'rgba(245,248,255,0.92)',
              boxShadow: '0 8px 18px rgba(0,0,0,0.22)',
              backdropFilter: 'blur(6px)',
              transition: 'all 160ms ease',
              whiteSpace: 'nowrap',
              fontSize: 11,
              lineHeight: 1.2,
            }}
            aria-label="BBFO legend"
          >
            <span style={{ fontWeight: 700, letterSpacing: '0.08em' }}>BBFO</span>
            <span
              style={{
                maxWidth: isBfoLegendHovered ? 320 : 0,
                opacity: isBfoLegendHovered ? 1 : 0,
                overflow: 'hidden',
                transition: 'max-width 160ms ease, opacity 160ms ease',
              }}
              aria-hidden={!isBfoLegendHovered}
            >
              <span>
                <i>Barely</i> Basic Formal Ontology
              </span>
            </span>
          </div>
        </div>
      )}
      {dimensions.width > 0 && dimensions.height > 0 && (
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          opacity: showBfoOverlay && (hoveredBfoClassId !== null || emphasizeEntireBfoUnderlay) ? 0.42 : 1,
          transition: 'opacity 700ms ease',
        }}
      >
      <ForceGraph3D
        ref={fgRef}
        width={dimensions.width}
        height={dimensions.height}
        backgroundColor="rgba(0,0,0,0)"
        rendererConfig={{ antialias: true, alpha: true }}
        graphData={visibleData}
        showNavInfo={false}
        nodeId="id"
        linkSource="source"
        linkTarget="target"
        onEngineStop={handleEngineStop}
        onEngineTick={handleEngineTick}
        nodeThreeObject={(node: object) => {
          const n = node as InternalNode;
          const isPreviewNode = previewNodeIds.has(n.id);
          // Ghost nodes: render as a tiny invisible point (no label).
          if (ghostNodeIds.has(n.id)) {
            const sprite = new SpriteText('');
            sprite.textHeight = 0;
            sprite.backgroundColor = 'rgba(0,0,0,0)';
            return sprite;
          }
          const hasChildren = isTreeMode && n.childLinks.length > 0;
          const label = n.name ?? n.id;
          const displayLabel = (hasChildren && n.collapsed)
            ? `${label} ▶`
            : label;
          const sprite = new SpriteText(displayLabel);
          sprite.color = '#ffffff';
          // The 3D view has no stroke or radius to vary, so the focused node is
          // distinguished by a larger label to match the thicker ring used in 2D.
          const isFocusedHighlight = (highlightedNodeScores.get(n.id) ?? 0) >= 0.9;
          sprite.textHeight = isPreviewNode ? 5.5 : isFocusedHighlight ? 5.5 : 4;
          sprite.fontWeight = 'bold';
          sprite.backgroundColor = isPreviewNode
            ? 'rgba(255,79,216,0.92)'
            : hasChildren
              ? (n.collapsed ? 'rgba(220,80,60,0.75)' : 'rgba(40,120,200,0.75)')
              : 'rgba(0,0,0,0.55)';
          sprite.padding = 2;
          sprite.borderRadius = 3;
          (sprite as unknown as { position: { y: number } }).position.y = 8;
          return sprite;
        }}
        nodeThreeObjectExtend={true}
        nodeLabel={(node) => {
          const n = node as InternalNode;
          if (ghostNodeIds.has(n.id)) return '';
          return formatNodeHoverLabel(n);
        }}
        nodeColor={(node) => {
          const n = node as InternalNode;
          if (ghostNodeIds.has(n.id)) return 'rgba(0,0,0,0)';
          if (relationshipCandidateNodeIds.includes(n.id)) {
            return '#39ff14';
          }
          const highlightScore = highlightedNodeScores.get(n.id);
          if (highlightScore !== undefined) return scoreToHighlightColor(highlightScore);
          if (schemaMode && hoveredSchemaNodeId && hoveredSchemaNodeId !== n.id) {
            return 'rgba(95,110,140,0.45)';
          }
          const baseColor = n.color ?? resolveNodeBaseColor(n);
          return baseColor;
        }}
        linkLabel={(link) => formatRelationshipHoverLabel(link as InternalLink)}
        linkColor={(link) => {
          const l = link as InternalLink;
          if (ghostNodeIds.has(l._parentId) || ghostNodeIds.has(l._childId)) {
            return 'rgba(255,160,60,0.6)';
          }
          if (isPreviewLink(l)) {
            return 'rgba(255,79,216,0.95)';
          }
          if (schemaMode && hoveredSchemaNodeId) {
            return l._parentId === hoveredSchemaNodeId || l._childId === hoveredSchemaNodeId
              ? 'rgba(220,230,255,0.9)'
              : 'rgba(130,140,160,0.22)';
          }
          return 'rgba(180,180,180,0.5)';
        }}
        linkDirectionalArrowLength={4}
        linkDirectionalArrowRelPos={1}
        linkDirectionalParticles={(link) => (isPreviewLink(link as InternalLink) ? 4 : 0)}
        linkDirectionalParticleWidth={(link) => (isPreviewLink(link as InternalLink) ? 3 : 0)}
        linkDirectionalParticleColor={() => PREVIEW_ACCENT}
        linkOpacity={0.6}
        onNodeClick={handleNodeClick as (node: object) => void}
        onNodeHover={handleNodeHover as (node: object | null) => void}
        onBackgroundClick={onBackgroundClick}
        onNodeRightClick={handleNodeRightClick as (node: object, event: MouseEvent) => void}
        onLinkRightClick={handleLinkRightClick as (link: object, event: MouseEvent) => void}
        linkWidth={(link) => {
          const internalLink = link as InternalLink;
          if (schemaMode && hoveredSchemaNodeId) {
            return internalLink._parentId === hoveredSchemaNodeId || internalLink._childId === hoveredSchemaNodeId ? 3.5 : 1.25;
          }
          return isPreviewLink(internalLink) ? 4 : 2;
        }}
        d3AlphaMin={0.05}
        d3VelocityDecay={0.4}
        warmupTicks={100}
        cooldownTicks={200}
        cooldownTime={5000}
      />
      </div>
      )}
    </div>
  );
};

export default ForceGraph3DView;
